import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Radio, RadioGroup } from '@headlessui/react';
import { createContext, useContext, type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { pillSwitchColorClasses } from '../lib/colorSchemes';
import { cn } from '../lib/utils';

type Color = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';
type Size = 'sm' | 'md' | 'lg';

// ── Variants ──────────────────────────────────────────────────────────────────

const pillSwitchVariants = tv({
  slots: {
    root: 'inline-flex rounded-full p-1 gap-0.5',
    option: [
      'inline-flex items-center gap-1.5 rounded-full font-medium cursor-pointer select-none',
      'transition-colors [outline:none] focus-visible:ring-2 focus-visible:ring-offset-1',
      'disabled:pointer-events-none disabled:opacity-50'
    ]
  },
  variants: {
    size: {
      sm: { option: 'px-2.5 py-0.5 text-xs' },
      md: { option: 'px-3.5 py-1.5 text-sm' },
      lg: { option: 'px-5 py-2 text-base' }
    },
    color: pillSwitchColorClasses
  },
  defaultVariants: {
    size: 'md',
    color: 'neutral'
  }
});

type PillSwitchVariants = VariantProps<typeof pillSwitchVariants>;

// ── Context ───────────────────────────────────────────────────────────────────

type PillSwitchCtx = { color: Color; size: Size };

const PillSwitchContext = createContext<PillSwitchCtx>({ color: 'neutral', size: 'md' });

// ── PillSwitch ────────────────────────────────────────────────────────────────

interface PillSwitchProps {
  value?: string;
  onChange?: (value: string) => void;
  color?: Color;
  size?: Size;
  disabled?: boolean;
  children?: ReactNode;
  className?: string;
}

function PillSwitch({
  value,
  onChange,
  color = 'neutral',
  size = 'md',
  disabled,
  children,
  className
}: PillSwitchProps) {
  const { root } = pillSwitchVariants({ color, size });
  return (
    <PillSwitchContext.Provider value={{ color, size }}>
      <RadioGroup
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={cn(root(), className)}>
        {children}
      </RadioGroup>
    </PillSwitchContext.Provider>
  );
}

// ── PillSwitchOption ──────────────────────────────────────────────────────────

interface PillSwitchOptionProps {
  value: string;
  children?: ReactNode;
  /** Optional icon rendered before the label */
  icon?: IconDefinition;
  disabled?: boolean;
  className?: string;
}

function PillSwitchOption({ value, children, icon, disabled, className }: PillSwitchOptionProps) {
  const { color, size } = useContext(PillSwitchContext);
  const { option } = pillSwitchVariants({ color, size });
  return (
    <Radio value={value} disabled={disabled} className={cn(option(), className)}>
      {icon && <FontAwesomeIcon icon={icon} className="shrink-0" />}
      {children}
    </Radio>
  );
}

export { PillSwitch, PillSwitchOption, pillSwitchVariants };
export type { PillSwitchProps, PillSwitchOptionProps, PillSwitchVariants };
